import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { getUsersEntities } from '../../../../store';
import { IStudentEditableField } from '../../../constants/paths/student-editable-map.constant';
import { SorterColumnDataType } from '../../../constants/sorter-column-data-type.constant';
import { BatchEditService } from '../../../services/batch-edit-service/batch-edit-service';
import { IUserMiniWithRole } from '../../../typings/interfaces/user.interface';
import { IEditableRecord } from './../../../services/batch-edit-service/batch-edit-service';

@Component({
  selector: 'batch-edit-modal-input',
  template: require('./batch-edit-modal-input.component.html'),
  styles: [require('./batch-edit-modal-input.component.scss')],
})
export class BatchEditModalInputComponent implements OnInit, OnDestroy {
  // conditions
  public isInput: boolean;
  public isCheckbox: boolean;
  public isDropdown: boolean;
  public isAutoComplete: boolean;
  public isArray: boolean;
  public isRadio: boolean;
  public inputGroup: FormGroup;
  public dropdownItems;
  public radioItems: string[];
  public triggerValue;
  public schoolUsers;
  public schoolUsersFilteredForPointPeople;

  public usersEntities$: Observable<IUserMiniWithRole[]>;
  public destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() field: IStudentEditableField;
  @Input() fieldSelected: boolean;
  @Input() valueSelected: boolean;
  @Input() newValue: any;
  @Output() newValueChange = new EventEmitter<any>();

  constructor(
    private formBuilder: FormBuilder,
    private store: Store<any>,
    private batchEditService: BatchEditService,
    @Inject(MAT_DIALOG_DATA) public data: any[],
  ) {
    this.dropdownItems = {
      areUsers: null,
      items: null,
    };
  }

  ngOnInit(): void {
    const field = cloneDeep(this.field);
    const { dataType } = field;
    this._clearCurrentFormFieldType();
    this._setFormFieldType(field);
    this.inputGroup = this._buildForm();
    this._setSchoolUsers();
    if (dataType !== SorterColumnDataType.STRING && dataType !== SorterColumnDataType.MILESTONE) {
      this._setDropdown(field);
    }
  }

  ngOnChanges(changes): void {
    const { field } = changes;
    if (field && !field.firstChange) {
      this._clearCurrentFormFieldType();
      this.resetInputType();
      this._setFormFieldType(field.currentValue);
      if (
        field.currentValue.dataType !== SorterColumnDataType.STRING &&
        field.currentValue.dataType !== SorterColumnDataType.MILESTONE
      ) {
        this._setDropdown(changes.field.currentValue);
      }
    }
  }

  ngOnDestroy(): void {
    this._clearCurrentFormFieldType();
    // this.destroy$.next(true);
    // this.destroy$.unsubscribe();
  }

  resetInputType() {
    this.isInput = false;
    this.isDropdown = false;
    this.isRadio = false;
    this.radioItems = null;
    this.isArray = false;
  }

  _setSchoolUsers(): void {
    this.usersEntities$ = this.store.select(getUsersEntities);
    this.usersEntities$.subscribe(users => {
      users = this.batchEditService.sortUsersByLastName(users);
      this.schoolUsers = users;
      this.schoolUsersFilteredForPointPeople = this.batchEditService.getAuthorizedPointPeople(users);
    });
  }

  _clearCurrentFormFieldType(): void {
    this.isInput = this.isCheckbox = this.isDropdown = this.isAutoComplete = this.isArray = false;
  }

  _setFormFieldType(currentValue: IEditableRecord): void {
    switch (currentValue.dataType) {
      case SorterColumnDataType.STRING:
        this.isInput = true;
        break;
      case SorterColumnDataType.BOOLEAN_YES_NO:
      case SorterColumnDataType.ENUM:
      case SorterColumnDataType.USER_MINI:
        this.isDropdown = true;
        break;
      case SorterColumnDataType.ARRAY:
        this.isArray = true;
        break;
      case SorterColumnDataType.MILESTONE:
        this.isRadio = true;
        this.radioItems = currentValue.dataTypeOptions.values;
        break;
      default:
        throw new Error('Invalid datatype value');
    }
  }

  _setDropdown(field) {
    let users: IUserMiniWithRole[];
    // advisor and guidance counselors have a path 'pointPeople', but can be assigned to any school user (JE)
    if (field.type === 'ADVISOR' || field.type === 'GUIDANCE_COUNSELOR') {
      users = this.batchEditService.getAuthorizedAdvisors(this.schoolUsers);
    } else if (field.path === 'pointPeople') {
      users = this.schoolUsersFilteredForPointPeople;
    } else {
      users = this.schoolUsers;
    }

    if (field.dataType === SorterColumnDataType.USER_MINI && users) {
      this.dropdownItems.areUsers = true;
      this.dropdownItems.items = this.batchEditService.getUsersDropdown(users);
    } else {
      this.dropdownItems.areUsers = false;
    }

    if (field.dataType === SorterColumnDataType.BOOLEAN_YES_NO) {
      this.dropdownItems.items = this.batchEditService.getBooleanDropdown();
    }

    if (field.dataType === SorterColumnDataType.ENUM) {
      this.dropdownItems.items = field.dataTypeOptions.values;
    }

    if (field.dataTypeOptions && field.dataTypeOptions.canBeNull && this.dropdownItems.items[0] !== '—') {
      this.dropdownItems.items.unshift('—');
    }
  }

  _buildForm(): FormGroup {
    const form = this.formBuilder.group({
      textarea: [null, Validators.required],
      dropdown: ['---', Validators.required],
    });
    return form;
  }

  onTextInput(): void {
    this.newValueChange.emit(this.inputGroup.controls.textarea.value);
  }

  onSelection(item): void {
    if (item && item.saveValue === null) {
      // for pointPeople fields
      this.newValue = null;
    }

    if (item && item.displayValue) {
      this.triggerValue = item.displayValue.name;
    } else if (item === '—') {
      // handle null value option for dropdowns (JE)
      this.triggerValue = item;
      this.newValue = null;
    } else {
      this.triggerValue = item;
      this.newValue = item;
    }
    this.newValueChange.emit(this.newValue);
  }
}
